import { useCallback, useEffect, useMemo, useRef } from 'react'

import { PolygonPlaneMeshProps } from '@editor/interfaces/canvas'
import {
  resetTransformControls,
  setAllowedTransformControls,
  setCurrentTransformType,
  setDisableTransformControls,
} from '@editor/pages/projects/editor/store/editor'
import { findObjectsOfType } from '@editor/services/Object3D'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { Group } from 'three'
import { TransformControls } from 'three-stdlib'

export function useTransforms({ polygon, transformControls, onUpdated, onReset }: PolygonPlaneMeshProps) {
  // Store
  const dispatch = useAppDispatch()
  const currentTransformType = useAppSelector((state) => state.editor.currentTransformType)
  const isAdjustingVertex = useAppSelector((state) => state.toolUtil.core.isAdjustingVertex)

  // TODO: Transformation pivot point. [0,0,0] should never happen as polygon always comes with center defined
  // const pivot: PointArray = useMemo(() => polygon.center || [0, 0, 0], [polygon.center])

  // Refs
  const transformControlsRef = useRef<TransformControls | null>(null)
  const controlledGroupRef = useRef<Group | null>(null)
  const isAnchorDown = useRef<boolean>(false)

  // The actual controlled object, one that is controlled by TransformControls
  // is something it added itself that we can't add a ref to, but what it does is
  // adding our own defined children (controlledGroupRef) as a child to that object,
  // so we just reference the parent to get it.
  const controlledObject = controlledGroupRef.current?.parent
  const anchors = useMemo(
    () => (controlledObject ? findObjectsOfType(controlledObject, 'CircleAnchor') : []),
    [controlledObject],
  )

  /**
   * Setup allowed transform types
   */
  useEffect(() => {
    if (!transformControls?.allowedTransforms) return () => null

    dispatch(setAllowedTransformControls(transformControls.allowedTransforms))
    dispatch(setCurrentTransformType(transformControls.allowedTransforms[0]))

    return () => {
      dispatch(resetTransformControls())
    }
  }, [transformControls?.allowedTransforms, dispatch])

  /**
   * Disable transform controls when it cannot be used.
   *
   * The flag will be used in other components to sync with transform controls state.
   */
  useEffect(() => {
    dispatch(setDisableTransformControls(transformControls?.enabled !== undefined ? !transformControls.enabled : false))
  }, [transformControls, dispatch])

  /**
   * Change transform mode when currentTransformType changes.
   */
  useEffect(() => {
    if (!transformControlsRef.current) return

    transformControlsRef.current.setMode(currentTransformType)
  }, [currentTransformType])

  /**
   * Hides transform controls when a vertex is being adjusted.
   */
  useEffect(() => {
    if (!transformControlsRef.current) return

    transformControlsRef.current.visible = !isAdjustingVertex
  }, [isAdjustingVertex])

  const toggleAnchors = useCallback(
    (visible: boolean) => {
      anchors.forEach((anchor) => {
        anchor.visible = visible
      })
    },
    [anchors],
  )

  /**
   * Handle mouse down on transform controls.
   *
   * We want to hide/disable any other possible interactions with the plane
   * to avoid conflicts while transforming.
   */
  const handleMouseDown = useCallback(() => {
    if (isAnchorDown.current) return
    toggleAnchors(false)
  }, [toggleAnchors])

  /**
   * Handle mouse up on transform controls.
   *
   * Restore whatever we hide/disabled on mouse down.
   * Also trigger onUpdated callback with latest positions and matrix.
   */
  const handleMouseUp = useCallback(() => {
    if (!controlledObject) return

    toggleAnchors(true)

    if (onUpdated) {
      onUpdated(polygon.positions, controlledObject.matrix)
    }
  }, [controlledObject, polygon.positions, toggleAnchors, onUpdated])

  /**
   * Handle reset transforms event.
   */
  useEffect(() => {
    const handleReset = () => {
      onReset?.()
    }

    document.addEventListener('transform-controls:reset', handleReset)

    return () => {
      document.removeEventListener('transform-controls:reset', handleReset)
    }
  }, [onReset])

  return {
    transformControlsRef,
    controlledGroupRef,
    isAnchorDown,

    toggleAnchors,

    handleMouseUp,
    handleMouseDown,
  }
}
